/**
 * The client's match: owns the shared MatchRunner, assigns input sequence numbers, and keeps the
 * previous and current states so rendering can interpolate between simulation ticks.
 */
import { botDriver } from '@steelwake/shared/ai';
import type { ArenaColliders } from '@steelwake/shared/arena';
import {
  MatchRunner,
  type BotLevel,
  type MatchEvent,
  type MatchSetup,
  type MatchState,
  type Slot,
} from '@steelwake/shared/combat';
import type { MatchWorld } from '@steelwake/shared/sim';

import type { InputSample } from '../sim/input.js';
import type { MatchParams } from './setup.js';

export interface SessionOptions {
  /** Each seat's loadout; the module ids reach the rules (Phase 6). */
  loadouts?: MatchSetup['loadouts'];
  /** The published balance document the rules run under (Phase 7). */
  balance?: MatchSetup['balance'];
  params: MatchParams;
  arena: ArenaColliders;
  world: MatchWorld;
  /** Practice cheats (ultimate ready) are honoured only in e2e mode. */
  e2e: boolean;
}

export interface InterpolatedPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  aimYaw: number;
  aimPitch: number;
}

function wrap(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

export function setupFromParams(
  params: MatchParams,
  arena: ArenaColliders,
  loadouts: MatchSetup['loadouts'] = undefined,
  balance: MatchSetup['balance'] = undefined,
): MatchSetup {
  const spawns = arena.spawns.map((s) => ({
    position: { x: s.position[0], y: s.position[1], z: s.position[2] },
    yaw: s.yaw,
  }));
  const a = spawns[0];
  const b = spawns[1];
  if (!a || !b) throw new Error('arena needs two spawns');
  return {
    seed: params.seed,
    arenaId: arena.arena,
    mechs: [params.mech, params.opponent],
    ...(loadouts ? { loadouts } : {}),
    ...(balance ? { balance } : {}),
    spawns: [a, b],
    config: {
      mode: 'practice',
      roundSeconds: params.roundSeconds,
      countdownSeconds: params.countdownSeconds,
      roundsToWin: params.roundsToWin,
      bots: [null, params.bot],
    },
  };
}

export class MatchSession {
  readonly mySlot: Slot = 0;
  readonly enemySlot: Slot = 1;
  private runner: MatchRunner;
  private previous: MatchState;
  private seq = 1;
  private readonly world: MatchWorld;
  private readonly arena: ArenaColliders;
  private readonly e2e: boolean;
  private params: MatchParams;

  private readonly loadouts: MatchSetup['loadouts'];

  private readonly balance: MatchSetup['balance'];

  constructor(options: SessionOptions) {
    this.loadouts = options.loadouts;
    this.balance = options.balance;
    this.world = options.world;
    this.arena = options.arena;
    this.e2e = options.e2e;
    this.params = options.params;
    this.runner = this.createRunner(options.params);
    this.previous = this.runner.state;
  }

  private createRunner(params: MatchParams): MatchRunner {
    const runner = new MatchRunner(
      setupFromParams(params, this.arena, this.loadouts, this.balance),
      this.world,
      this.arena,
      botDriver,
    );
    if (params.ultReady && this.e2e) runner.ultimateReady(0);
    return runner;
  }

  get state(): MatchState {
    return this.runner.state;
  }

  get prev(): MatchState {
    return this.previous;
  }

  get setup(): MatchParams {
    return this.params;
  }

  get finished(): boolean {
    return this.runner.finished;
  }

  /** One simulation tick with the player's sampled input. */
  step(sample: InputSample): MatchEvent[] {
    this.previous = this.runner.state;
    return this.runner.step([{ ...sample, seq: this.seq++ }, null]);
  }

  /** Same setup, new seed, no asset reload. */
  rematch(seed: number): void {
    this.params = { ...this.params, seed };
    this.runner = this.createRunner(this.params);
    this.previous = this.runner.state;
    this.seq = 1;
  }

  /** Interpolated body pose of a slot; snaps on a respawn (a jump larger than any tick could move). */
  pose(slot: Slot, alpha: number): InterpolatedPose {
    const a = this.previous.mechs[slot].body;
    const b = this.runner.state.mechs[slot].body;
    const jump = Math.hypot(b.position.x - a.position.x, b.position.z - a.position.z);
    if (jump > 8 || this.previous.flow.round !== this.runner.state.flow.round) {
      return { ...b.position, yaw: b.yaw, aimYaw: b.aimYaw, aimPitch: b.aimPitch };
    }
    const k = Math.max(0, Math.min(1, alpha));
    return {
      x: a.position.x + (b.position.x - a.position.x) * k,
      y: a.position.y + (b.position.y - a.position.y) * k,
      z: a.position.z + (b.position.z - a.position.z) * k,
      yaw: a.yaw + wrap(b.yaw - a.yaw) * k,
      aimYaw: a.aimYaw + wrap(b.aimYaw - a.aimYaw) * k,
      aimPitch: a.aimPitch + (b.aimPitch - a.aimPitch) * k,
    };
  }

  /** Interpolated projectile positions by id (new projectiles appear at their current position). */
  projectiles(alpha: number): { id: number; x: number; y: number; z: number; weapon: string }[] {
    const k = Math.max(0, Math.min(1, alpha));
    const prevById = new Map(this.previous.projectiles.map((p) => [p.id, p]));
    return this.runner.state.projectiles.map((p) => {
      const q = prevById.get(p.id);
      if (!q)
        return { id: p.id, x: p.position.x, y: p.position.y, z: p.position.z, weapon: p.weapon };
      return {
        id: p.id,
        x: q.position.x + (p.position.x - q.position.x) * k,
        y: q.position.y + (p.position.y - q.position.y) * k,
        z: q.position.z + (p.position.z - q.position.z) * k,
        weapon: p.weapon,
      };
    });
  }

  // --- practice hooks (forwarded to the runner, which refuses them outside practice) ---

  setBotLevel(level: BotLevel): void {
    this.runner.setBotLevel(this.enemySlot, level);
    this.previous = this.runner.state;
  }

  skipCountdown(): void {
    this.runner.skipCountdown();
    this.previous = this.runner.state;
  }

  setRoundSeconds(seconds: number): void {
    this.runner.setRoundSeconds(seconds);
    this.previous = this.runner.state;
  }

  ultimateReady(): void {
    this.runner.ultimateReady(this.mySlot);
    this.previous = this.runner.state;
  }

  teleport(position: { x: number; y: number; z: number }): void {
    this.runner.teleport(this.mySlot, position);
  }
}
